import { useToast } from '@vimeo/bokeh';
import { useGetVideo } from '@vimeo/gctl-api/hooks/useVideo';
import { VideoFields } from '@vimeo/gctl-api/models/components/schemas/video/Video';
import { translate } from '@vimeo/i18n-runtime';
import { useGetAlbumVideoData } from '@vimeo/use-get-album-video';
import { useGetUnlockedVideo, usePatchUnlockedVideo } from '@vimeo/use-unlocked-video';

import { AnalyticsPropsType } from '../../CommentsCommon/types';
import { bpAllowViewerComments } from '../../utils/analytics';
import {
  extractClipData,
  getReviewPasswordHashFromCookie,
  getVideoIdFromClipRequestId,
  mapToClipFields,
} from '../../utils/helper';
import { useViewer } from '../use-viewer';

export const useVideoPrivacy = (clipRequestId: string, showcaseId?: number, reviewId?: string) => {
  const viewer = useViewer();

  const { data: privacyVideoData, isLoading } = useVideoDataForPrivacy(
    clipRequestId,
    showcaseId,
    reviewId,
  );

  const toast = useToast();
  const [patchVideo] = usePatchUnlockedVideo();

  const saveViewerCommentsSetting = async (isAllowed: boolean) => {
    await patchVideo({
      // @ts-expect-error videoId expects a number, but doesn't account for clipHash strings
      where: { videoId: clipRequestId },
      select: ['privacy'],
      variables: { privacy: { comments: isAllowed ? 'anybody' : 'nobody' } },
    }).then(() => {
      let analytics: AnalyticsPropsType = {
        webContextFields: {
          page_name: 'single_video_view_manage',
        },
      };
      bpAllowViewerComments(
        isAllowed,
        'Allow Viewer Comments',
        clipRequestId,
        false,
        privacyVideoData?.privacy?.view,
        analytics,
        privacyVideoData?.user?.uri,
        viewer,
      );
      toast({ duration: 3000, title: translate('Preferences updated') });
    });
  };

  return {
    privacy: privacyVideoData?.privacy,
    isLoading,
    saveViewerCommentsSetting,
  };
};

/**
 * Custom hook to fetch video privacy data based on the context (regular video, review link, or showcase)
 */
const useVideoDataForPrivacy = (clipRequestId: string, showcaseId?: number, reviewId?: string) => {
  const viewer = useViewer();
  const selectFields = ['privacy'] as VideoFields[];

  const reviewVideoData = useGetVideo(
    // @ts-expect-error videoId expects a number, but doesn't account for clipHash strings
    () => {
      if (!viewer || !clipRequestId || !reviewId || showcaseId) return null;

      const password = getReviewPasswordHashFromCookie(reviewId);
      return {
        where: { videoId: clipRequestId },
        select: selectFields,
        query: { reviewId, password },
      };
    },
    {
      revalidateOnFocus: false,
    },
  );

  const albumVideoData = useGetAlbumVideoData(
    showcaseId ? showcaseId : null,
    Number(getVideoIdFromClipRequestId(clipRequestId)),
    mapToClipFields(selectFields),
  );

  const videoData = useGetUnlockedVideo(
    // @ts-expect-error videoId expects a number, but doesn't account for clipHash strings
    () => {
      if (!viewer || showcaseId || reviewId) return null;

      return {
        where: { videoId: clipRequestId },
        select: selectFields,
      };
    },
    {
      revalidateOnFocus: false,
    },
  );

  if (reviewId) {
    return reviewVideoData;
  }

  if (showcaseId) {
    return {
      data: extractClipData(albumVideoData.data as Record<string, unknown>),
      isLoading: albumVideoData.isLoading,
    };
  }

  return videoData;
};
